import {
  type ShareUrlGeneratedComments,
  type ShareUrlGeneratedCustomFields,
  type ShareUrlUpdatedDownloads,
  type ShareUrlUpdatedSetting,
  type ShareUrlUpdatedVersionControl,
} from '@air/analytics';

import { type PermissionMode } from '~/components/ShowcaseModal/types';
import { type DownloadPermissionMode } from '~/utils/shortUrl/buildDownloadPermissionShortUrlUpdate';

export const shareUrlUpdatedLinkAccess = (enabled: boolean): ShareUrlUpdatedSetting => ({
  updated_setting: 'link_access',
  link_access: enabled ? 'enabled' : 'disabled',
});

export const shareUrlUpdatedMetadata = (metadata: boolean): ShareUrlUpdatedSetting => ({
  updated_setting: 'metadata',
  metadata,
});

const toShareUrlUpdatedComments = (canView: boolean, canManage: boolean): ShareUrlGeneratedComments => {
  if (canView && canManage) {
    return 'allow';
  }

  if (canView) {
    return 'read_only';
  }

  return 'hide';
};

const toShareUrlUpdatedCustomFields = (
  canView: boolean,
  canManageApplications: boolean,
): ShareUrlGeneratedCustomFields => {
  if (canView && canManageApplications) {
    return 'editable';
  }

  if (canView) {
    return 'read_only';
  }

  return 'hide';
};

export const shareUrlUpdatedCommentsFromBooleans = (
  canViewDiscussions: boolean,
  canManageDiscussions: boolean,
): ShareUrlUpdatedSetting => ({
  updated_setting: 'comments',
  comments: toShareUrlUpdatedComments(canViewDiscussions, canManageDiscussions),
});

const permissionModeToComments = (mode: PermissionMode): ShareUrlGeneratedComments => {
  if (mode === 'allow') {
    return 'allow';
  }

  if (mode === 'read-only') {
    return 'read_only';
  }

  return 'hide';
};

const permissionModeToCustomFields = (mode: PermissionMode): ShareUrlGeneratedCustomFields => {
  if (mode === 'allow') {
    return 'editable';
  }

  if (mode === 'read-only') {
    return 'read_only';
  }

  return 'hide';
};

export const shareUrlUpdatedCommentsFromPermissionMode = (mode: PermissionMode): ShareUrlUpdatedSetting => ({
  updated_setting: 'comments',
  comments: permissionModeToComments(mode),
});

export const shareUrlUpdatedCustomFieldsFromBooleans = (
  canView: boolean,
  canManageApplications: boolean,
): ShareUrlUpdatedSetting => ({
  updated_setting: 'custom_fields',
  custom_fields: toShareUrlUpdatedCustomFields(canView, canManageApplications),
});

export const shareUrlUpdatedCustomFieldsFromPermissionMode = (mode: PermissionMode): ShareUrlUpdatedSetting => ({
  updated_setting: 'custom_fields',
  custom_fields: permissionModeToCustomFields(mode),
});

export const shareUrlUpdatedHideSearchInput = (hideSearchInput: boolean): ShareUrlUpdatedSetting => ({
  updated_setting: 'hide_search_input',
  hide_search_input: hideSearchInput,
});

export const shareUrlUpdatedHideAirLogo = (hideAirLogo: boolean): ShareUrlUpdatedSetting => ({
  updated_setting: 'hide_air_logo',
  hide_air_logo: hideAirLogo,
});

export const shareUrlUpdatedDisableViewSettings = (disableViewSettings: boolean): ShareUrlUpdatedSetting => ({
  updated_setting: 'disable_view_settings',
  disable_view_settings: disableViewSettings,
});

export const shareUrlUpdatedDisableExpandingNested = (disableExpandingNested: boolean): ShareUrlUpdatedSetting => ({
  updated_setting: 'disable_expanding_nested',
  disable_expanding_nested: disableExpandingNested,
});

export const downloadPermissionModeToAnalytics = (mode: DownloadPermissionMode): ShareUrlUpdatedDownloads => {
  if (mode === 'single-file') {
    return 'single_file';
  }

  if (mode === 'all-assets') {
    return 'all_assets';
  }

  return 'none';
};

export const shareUrlUpdatedDownloadsFromMode = (mode: DownloadPermissionMode): ShareUrlUpdatedSetting => ({
  updated_setting: 'downloads',
  downloads: downloadPermissionModeToAnalytics(mode),
});

export const shareUrlUpdatedDownloadsFromClipToggle = (enabled: boolean): ShareUrlUpdatedSetting => ({
  updated_setting: 'downloads',
  downloads: enabled ? 'single_file' : 'none',
});

export const versionControlToAnalytics = (
  canViewAllVersions: boolean,
  variant: 'multi-asset' | 'single-asset',
): ShareUrlUpdatedVersionControl => {
  if (canViewAllVersions) {
    return 'all_versions';
  }

  if (variant === 'single-asset') {
    return 'this_version';
  }

  return 'latest_only';
};

export const shareUrlUpdatedVersionControl = (
  canViewAllVersions: boolean,
  variant: 'multi-asset' | 'single-asset',
): ShareUrlUpdatedSetting => ({
  updated_setting: 'version_control',
  version_control: versionControlToAnalytics(canViewAllVersions, variant),
});
